import { useMemo, useEffect, useState } from "react";
import * as Typo from "typo-js";
import { alternativeNormalize, closestParentOfType, goToLine } from "../../../services/editor-manipulation";
import { splitPhrases, latinPhrases, customWords } from "./lis-dictionary-addendum";
const CURRENTLY_SHOWING_OPTIONS = {
    MISSPELLINGS: 'MISSPELLINGS',
    PERSONAL_DICTIONARY: 'PERSONAL_DICTIONARY',
}

const SpellCheck = props => {

    const [dictionary, setDictionary] = useState(null);
    const [misspellings, setMisspellings] = useState(new Map());

    const [scan, toggleScan] = useState(false);

    const [personalDictionary, setPersonalDictionary] = useState([]);

    const [isNormalized, toggleNormalized] = useState(false);

    const [currentlyShowing, setCurrentlyShowing] = useState(null);

    const [isDone, toggleDone] = useState(false);

    const spellcheckTooltip = props.members.length === 0 ? "Waiting for members to load ... " : props.allowGoToMisspelling ? '' : 'Spellcheck must be run after making updates to see the changes or go to an instance.';

    const [areYouSure, toggleAreYouSure] = useState({ show: false, word: '', action: null });

    const problemNames = useMemo(() => {
        return (props.members ?? []).filter(member => member.includes(' ')).map(m => m.toLowerCase());
    }, [props.members]);

    const misspellingEntries = useMemo(() => {

        const addToDictionary = (word) => {
            toggleAreYouSure({ show: true, word, action: 'add' });
        }

        const selectText = (word) => {
            if (word.parent.parentElement?.nodeName === 'SPAN') {
                const rangeRect = word.parent.parentElement.getBoundingClientRect();
                const newY = rangeRect.y + window.scrollY - 100;
                window.scrollTo(rangeRect.x, newY);
                const otherSelected = document.querySelectorAll('.selected');
                otherSelected.forEach(os => {
                    os.classList.remove('selected');
                });
                word.parent.parentElement.classList.add('selected');
            } else if (word.lineNumber) {
                goToLine(word.lineNumber)
            } else {
                props.makeToast([{ message: "Paragraph has been touched since last checked. Please run spellcheck again.", type: "warning" }])
            }
        }

        const copySuccessful = () => props.makeToast([{ message: "Copied to clipboard", type: "success" }]);

        const r = [];
        const bads = []
        misspellings.forEach((v, k) => {
            const bad = v.filter(w => w.startIndex < 0 || w.endIndex < 0);
            const goods = v.filter(w => w.startIndex >= 0 || w.endIndex >= 0);
            // Forget why this was even here
            bad.forEach(w => {
                bads.push(w);
            })
            r.push(<MisspellingEntry key={k} k={k} v={v} goods={goods} dictionary={dictionary} copySuccessful={copySuccessful}
                addToDictionary={addToDictionary} selectText={selectText} spellcheckTooltip={spellcheckTooltip} />);
        });
        return r;
    }, [misspellings, props.allowGoToMisspelling]);

    useEffect(() => {
        const ms = new Map(misspellings);
        const containerDiv = document.getElementById(props.editorDiv);
        const subDivs = Array.from(containerDiv.children);
        const originalPs = subDivs.flatMap(sd => Array.from(sd.getElementsByTagName('p')))
        if (props.selectedP && ms.size > 0) {
            ms.forEach((v, k) => {
                const entries = v.map(w => ({
                    ...w,
                    canNavigate: w.parent.parentElement?.nodeName === 'SPAN'
                }))
                    .map(w => recalculateLineNumber(w, originalPs, props.lines.flatMap(l => getPsFromLineNumbers(l))));
                ms.set(k, entries);
            })
        }
        setMisspellings(ms);
    }, [props.selectedP])

    useEffect(() => {
        if (props.members.length > 0) {
            const filesExt = window.env?.APP_ENV.toLowerCase() === 'production' ? '' : window.env?.APP_ENV.toLowerCase();
            const newDictionary = new Typo(`we hardcode the name in the node_modules now`, false, false, {
                dictionaryPath: `${window.env?.STORAGE_ACCOUNT_DOMAIN}/files${filesExt}`
            });
            // Add a custom word
            // newDictionary.dictionaryTable['2025'] = null;
            customWords.forEach(word => {
                newDictionary.dictionaryTable[word] = null;
            });

            const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
            setPersonalDictionary(fromStorage);
            fromStorage.forEach(word => {
                newDictionary.dictionaryTable[word] = null;
            });
            props.members.filter(m => !m.includes(' ')).forEach(m => {
                newDictionary.dictionaryTable[m] = null;
            })
            setDictionary(newDictionary);
        }
    }, [props.members]);

    useEffect(() => {
        if (props.editorsInitialized && dictionary && scan) {
            toggleNormalized(false);
            fireNormalize();
            toggleScan(false);
            const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
            setPersonalDictionary(fromStorage);
        }
    }, [props.editorsInitialized, dictionary, scan]);

    useEffect(() => {
        if (isNormalized) {
            scanForMisspelledWords();
        }
    }, [isNormalized]);

    useEffect(() => {
        if (currentlyShowing === CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS) {
            fireNormalize();
        } else {

        }
    }, [currentlyShowing]);

    const handleCheckSpelling = (e) => {
        e.preventDefault();
        if (props.members.length === 0) {
            return;
        }
        // Not sure if its needed, but can't hurt.
        document.getElementsByClassName('mce-content-body')[0]?.blur();
        document.getSelection().removeAllRanges();
        props.toggleAllowGoToMisspelling(true);
        toggleDone(false);
        if (CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS === currentlyShowing) {
            fireNormalize(true);
        } else {
            setCurrentlyShowing(CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS)
        }
    }

    // There is a corresponding div/table/p in the lines for every div/table/p in the editor.
    // UNLESS its a p in a table, in which case there is one p per column in the editor and one p total in the lines.
    // Add in fake p's for the tables.
    const getPsFromLineNumbers = (current, paddingPs) => {
        if (current === null) {
            return null
        }
        if (current.type === 'p') {
            if (paddingPs) {
                const padding = [];
                for (let i = 0; i < paddingPs; i++) {
                    padding.push(current.props.children);
                }
                return [current.props.children].concat(padding);
            } else {
                return current.props.children;
            }
        } else if (current.type === 'table') { //get the index of the table
            const index = props.lines.filter(l => l.type === 'table').indexOf(current);
            const editorDiv = document.getElementById(props.editorDiv);
            if (editorDiv) {
                const tables = Array.from(editorDiv.getElementsByTagName('table'));
                const table = tables[index];
                if (table && table.rows && table.rows.length > 0 && table.rows[0].cells && table.rows[0].cells.length > 0) {
                    const columnCount = table.rows[0].cells.length - 1;
                    return current.props.children ?
                        Array.isArray(current.props.children) ?
                            current.props.children.flatMap(currentChild => getPsFromLineNumbers(currentChild, columnCount)) :
                            getPsFromLineNumbers(current.props.children, columnCount)
                        : null;
                }
            }
            return null;

        } else {
            return current.props.children ?
                Array.isArray(current.props.children) ?
                    current.props.children.flatMap(currentChild => getPsFromLineNumbers(currentChild, paddingPs)) :
                    getPsFromLineNumbers(current.props.children, paddingPs)
                : null;
        }
    }

    const recalculateLineNumber = (word, originalPs, justThePs) => {
        const closestP = closestParentOfType(word.parent, 'P');
        if (closestP) {
            const originalIndex = originalPs.indexOf(closestP);
            const lineNumberIndex = Math.floor((word.parent.parentElement.getBoundingClientRect().y - closestP.getBoundingClientRect().y) / 32);
            if (justThePs[originalIndex]) { // tables ...
                const lineNumber = justThePs[originalIndex].split('\n')[lineNumberIndex];
                return {
                    ...word,
                    lineNumber
                }
            }
            else {
                return word;
            }
        } else {
            return word;
        }
    }

    const scanForMisspelledWords = (e) => {
        const latinPhasesAndNames = latinPhrases.concat(problemNames);

        const splitPhrasesAndNames = splitPhrases.concat(problemNames.map(name => name.split(' ').toReversed()));

        const isStartOfSectionOrReferencingAnotherOrSubsection = (word) => // A2, S6, 2a, 5g
            (word.match(/[A-Z][0-9](\.|,)?/)?.length ?? word.match(/[0-9][a-z](\.|,)?/)?.length) > 0;

        const isRomanNumeralWithParenthesis = (word) => // (i), (vi), (iii)(a)
            word.match(/^\((?=[mdclxvi])m{0,4}(cm|cd|d?c{0,3})(xc|xl|l?x{0,3})(ix|iv|v?i{0,3})\)(\([a-z]\))?$/)?.length > 0;

        const isIRSCode = (word) => // 168(k), 168(l), 168(m), 1400L, and 1400N
            word.match(/\d+[A-Za-z]?(?:\([a-z]\))?/g);

        const isAbbreviatedSpecialSession = (word, i, words) => // Sp. Sess.
            0 < i && words[i - 1].original === 'Sp.' && word === 'Sess.';

        const validCharCode = (code) => {
            // 0-9
            return (code >= 48 && code <= 57)
                // A-Z
                || (code >= 65 && code <= 90)
                // a-z
                || (code >= 97 && code <= 122)
        }

        const cleanWord = (word) => {
            if (word.length === 0) {
                return word;
            }
            let newWord = word;
            if (validCharCode(newWord.charCodeAt(0))) {

            } else {
                newWord = newWord.substring(1);
                newWord = cleanWord(newWord);
            }
            if (validCharCode(newWord.charCodeAt(newWord.length - 1))) {

            } else {
                newWord = newWord.substring(0, newWord.length - 1);
                newWord = cleanWord(newWord);
            }
            return newWord;
        }

        // This doesn't work as intended, but it works ... so we are keeping it
        const getContentInFirstSection = (node, hasBeenLdtitle, hasBeenPatron) => {
            const closestP = closestParentOfType(node, 'P');
            let isLdTitle = hasBeenLdtitle;
            let isPatron = hasBeenPatron;
            if (closestP.className.toLowerCase().indexOf('ldtitle') > -1) {
                isLdTitle = true;
            } else if (closestP.className.toLowerCase().indexOf('billpatron') > -1) {
                isPatron = true;
            }
            else if (closestP.className.toLowerCase().indexOf('enactstm') > -1 || closestP.className.toLowerCase().indexOf('enactcls') > -1) {
                return { getContent: true, hasBeenLdtitle: isLdTitle, hasBeenPatron: isPatron };
            }
            return { getContent: isLdTitle !== isPatron, hasBeenLdtitle: isLdTitle, hasBeenPatron: isPatron };
        }

        const getContainerDetails = (theP, on, i) => {
            let n = on;
            const walker = document.createTreeWalker(theP, NodeFilter.SHOW_TEXT);
            const textNodes = [];
            let node;
            let hasBeenLdtitle = false;
            let hasBeenPatron = false;
            let getContent = i > 0;
            while (node = walker.nextNode()) {
                if (i === 0) {
                    const getContentDetails = getContentInFirstSection(node, hasBeenLdtitle, hasBeenPatron);
                    getContent = getContentDetails.getContent;
                    hasBeenLdtitle = getContentDetails.hasBeenLdtitle;
                    hasBeenPatron = getContentDetails.hasBeenPatron;
                }
                // Resolutions ... non introduced ... ?
                if (getContent) {
                    textNodes.push(node);
                }
            }
            const details = [];
            textNodes.forEach(tn => {
                const split = tn.textContent.split(/\s+/);
                let str = '';

                for (let i = 0; i < split.length; i++) {
                    str += split[i];
                    const startIndex = str.lastIndexOf(split[i]);
                    let endIndex = -1;
                    if (startIndex >= 0) {
                        endIndex = startIndex + split[i].length;
                    }
                    else {
                        str = str.substring(0, str.length - split[i].length);
                    }
                    if (i !== split.length - 1) {
                        str += ' ';
                    }
                    if (endIndex === -1 && tn.textContent.includes('"Tetrahydrocannabinol')) {
                        //debugger
                    }
                    details.push({
                        original: split[i],
                        cutDown: split[i],
                        parent: tn,
                        startIndex,
                        endIndex,
                        n,
                        indexes: getLocation(tn, theP.parentElement)
                    })
                    n++;
                }
            })
            return { separateWords: details, n };
        }

        const checkLatinPhrase = (words) => {
            const returnWords = [];
            words.forEach((word) => {
                const foundPhrases = splitPhrasesAndNames.map((phrase, k) => phrase[0] === word.cutDown.toLowerCase() ? k : -1).filter(k => k >= 0)
                    .map((k) => {
                        const startIndex = returnWords.length - splitPhrasesAndNames[k].length + 1;
                        if (startIndex >= 0) {
                            const newPhrase = returnWords.slice(startIndex, returnWords.length).map(w => w.cutDown.toLowerCase());
                            const phraseString = newPhrase.join(' ') + ' ' + word.cutDown.toLowerCase();
                            const latinPhrase = latinPhasesAndNames[k].toLowerCase();
                            return {
                                match: phraseString === latinPhrase,
                                word: word.cutDown, phraseString,
                                latin: latinPhasesAndNames[k],
                                startIndex,
                                n: word.n
                            };
                        } else {
                            return {}
                        }
                    })
                const matched = foundPhrases.find(f => f.match);
                if (matched) {
                    returnWords.length = matched.startIndex;
                }
                else {
                    returnWords.push(word);
                }
            })
            return returnWords;
        }

        // Remove empty strings that come in because of how pulling text from tags works.
        const getContext = (v, allWords) => {
            let preContext = '';
            let postContext = '';
            let previousValue = v.n - 1;
            let count = 0;
            const currentParent = closestParentOfType(v.parent, 'P');
            while (previousValue >= 0 && count < 3) {
                if (closestParentOfType(allWords[previousValue].parent, 'P') !== currentParent) {
                    break;
                }
                if (allWords[previousValue].original.length === 0) {
                    previousValue--;
                } else {
                    preContext = allWords[previousValue].original + ' ' + preContext;
                    previousValue--;
                    count++;
                }
            }
            let nextValue = v.n + 1;
            count = 0;
            while (nextValue < allWords.length && count < 3) {
                if (closestParentOfType(allWords[nextValue].parent, 'P') !== currentParent) {
                    break;
                }
                if (allWords[nextValue].original.length === 0) {
                    nextValue++;
                } else {
                    postContext += ' ' + allWords[nextValue].original;
                    nextValue++;
                    count++;
                }
            }
            return <>{preContext}<span style={{ color: 'red' }}>{v.original}</span>{postContext}</>
        }

        const getLocation = (textNode, closestDiv) => {
            const indexes = [];
            let currentNode = textNode;
            let parentElement = textNode.parentElement;
            while (parentElement && currentNode !== closestDiv) {
                const childNodes = Array.from(parentElement.childNodes);
                const index = childNodes.indexOf(currentNode);
                indexes.push(index);
                currentNode = parentElement;
                parentElement = parentElement.parentElement;
            }
            return indexes.reverse();
        }
        const counts = new Map();
        misspellings.forEach((v, k) => {
            const newV = v.filter(w => w.parent.parentElement?.nodeName === 'SPAN');
            if (newV.length > 0) {
                counts.set(k, newV);
            } else {
                counts.delete(k)
            }
        });
        const containerDiv = document.getElementById(props.editorDiv);
        const subDivs = Array.from(containerDiv.children);
        const originalPs = subDivs.flatMap(sd => Array.from(sd.getElementsByTagName('p')))
        const subPs = originalPs
            .filter(sp => sp.getElementsByClassName('spelling-error').length === 0)
            .filter((sp, i, me) => (i > 0 && i < me.length - 2) ?
                (!(me[i - 1].textContent === '—————' && me[i + 1].textContent === '—————') || sp.className.includes('billpatron')) : true)
        const allWords = [];
        let n = 0;
        const justThePs = props.lines.flatMap(l => getPsFromLineNumbers(l));
        for (let i = 0; i < subPs.length; i++) {
            const sp = subPs[i];
            let returnedDetails = getContainerDetails(sp, n, i);
            let savedN = n;
            n = returnedDetails.n;
            let separateWords = returnedDetails.separateWords;
            let words = separateWords
                .filter(w => customWords.indexOf(w.original) < 0)
                .filter(w => !isStartOfSectionOrReferencingAnotherOrSubsection(w.original))
                .filter(w => !isRomanNumeralWithParenthesis(w.original))
                .filter(w => !isIRSCode(w.original))
                .filter((w, i, arr) => !isAbbreviatedSpecialSession(w.original, i, arr))
                .map(w => ({ ...w, cutDown: cleanWord(w.cutDown) }))
                .filter(w => w.cutDown.length === 0 ||
                    (w.cutDown.match(/^[0-9]+(\.[0-9]+)?\-[0-9]+(\.[0-9]+(\:[0-9]+)?)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^([0-9]+,)?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^[0-9]+\/[0-9]+?$/g)?.length ?? 0) < 1)
                .flatMap(w => w.cutDown.split('-').map(w2 => ({
                    ...w,
                    cutDown: w2,
                })))
                .flatMap(w => w.cutDown.split('/').map(w2 => ({
                    ...w,
                    cutDown: w2,
                })))
                .filter(w => (w.cutDown.match(/^([0-9]+,)?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^([0-9]+,)*?[0-9]+(\.[0-9]+)?$/g)?.length ?? 0) < 1)
                .filter(w => (w.cutDown.match(/^[0-9]+\/[0-9]+?$/g)?.length ?? 0) < 1)
                .filter(w => w.cutDown.length > 0)
            words = checkLatinPhrase(words).filter(w => w.cutDown.length > 0)

            let misspelledWords = words.filter(word => {
                return !dictionary.check(word.cutDown);
            })
            const closestSpans = misspelledWords.map(m => closestParentOfType(m.parent, 'SPAN')).filter(s => s?.className.includes('spelling-error') > -1)
            if (closestSpans.length !== misspelledWords.length) {
                const newRange = document.createRange();
                const toSurround = misspelledWords[closestSpans.length];
                newRange.setStart(toSurround.parent, toSurround.startIndex);
                newRange.setEnd(toSurround.parent, toSurround.endIndex);
                const span = document.createElement('span')
                span.classList.add('spelling-error')
                newRange.surroundContents(span);
                i--;
                n = savedN;
                continue;
            }
            allWords.push(...separateWords);
            misspelledWords = misspelledWords.map(w => ({
                ...w,
                closestDiv: sp.parentElement,
                context: getContext(w, allWords),
                canNavigate: true
            }))
            misspelledWords.forEach(word => {
                if (counts.has(word.cutDown)) {
                    counts.set(word.cutDown, counts.get(word.cutDown).concat(word));
                } else {
                    counts.set(word.cutDown, [word]);
                }
            });
        }
        const sortedCounts = new Map();
        const firstInstances = [];
        counts.forEach((v, k) => {
            const sorted = v.toSorted((a, b) => a.parent.compareDocumentPosition(b.parent) === Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
            sortedCounts.set(k, sorted);
            firstInstances.push({ key: k, value: sorted[0] });
        });
        firstInstances.sort((a, b) => a.value.parent.compareDocumentPosition(b.value.parent) === Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1);
        const sortedSortedCounts = new Map();
        firstInstances.forEach(f => {
            // You can't get the contents at x,y of a given thing when its off the viewport.
            // So we need to parse through the JSX text content to get the calculated line number at a given index.
            // Have to do this here because otherwise the line numbers for those misspellings in unchanged paragraphs would be wrong.
            sortedSortedCounts.set(f.key, sortedCounts.get(f.key)
                .map((w, i) => recalculateLineNumber(w, originalPs, justThePs)));
        });
        setMisspellings(sortedSortedCounts);
        toggleNormalized(false);
        toggleDone(true);
    }

    const remove = (word) => {
        toggleAreYouSure({ show: true, word, action: 'remove' });
    }

    const fireNormalize = (keepMisspellings) => {
        const containerDiv = document.getElementById(props.editorDiv);
        const subDivs = Array.from(containerDiv.children);
        const subPs = subDivs.flatMap(sd => Array.from(sd.children));
        subPs.forEach(sp => {
            alternativeNormalize(sp, keepMisspellings);
        });
        toggleNormalized(true);
    }

    const promptGoToLine = () => {
        const lineNumber = prompt('Enter the line number to go to:', '')
        const number = parseInt(lineNumber, 10);
        if (!isNaN(number) && number > 0) {
            goToLine(number);
        }
    }

    const updateDictionary = (word) => {
        if (areYouSure.action === 'add') {
            setDictionary(prev => {
                prev.dictionaryTable[word] = null;
                const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
                fromStorage.push(word);
                localStorage.setItem('spellcheck', JSON.stringify(fromStorage));
                return prev;
            });
            toggleScan(true);
        } else {
            setDictionary(prev => {
                delete prev.dictionaryTable[word];
                return prev;
            });
            const fromStorage = JSON.parse(localStorage.getItem('spellcheck') ?? '[]');
            const newStorage = fromStorage.filter(w => w !== word);
            localStorage.setItem('spellcheck', JSON.stringify(newStorage));
            toggleScan(true);
        }
        toggleAreYouSure({ show: false, action: null, word: '' });
    }

    return <div className='side-panel' style={{ position: 'sticky', top: '215px' }}>
        {areYouSure.show && areYouSure.action && <div className="schedule-modal">
            <div className="schedule-modal-content">
                <p>{`You are about to ${areYouSure.action} ${areYouSure.word} ${areYouSure.action === 'add' ? 'to' : 'from'} your dictionary. Are you sure?`}</p>
                <button className="button secondary float-right" onClick={() => toggleAreYouSure({ show: false, action: null, word: '' })}>No</button>
                <button className="button primary float-right" onClick={() => updateDictionary(areYouSure.word)} style={{ marginRight: "15px" }}>Yes</button>
                <br />
            </div>
        </div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 1fr', gap: '10px' }}>
            <button className={'non-editor-button button' + (!props.allowGoToMisspelling ? ' reload' : ' no-reload')} onClick={(e) => handleCheckSpelling(e)} title={spellcheckTooltip}>{`Check Spelling ${!props.allowGoToMisspelling ? '\u27f3' : ''}`}</button>
            <button className='non-editor-button button' onClick={e => setCurrentlyShowing(CURRENTLY_SHOWING_OPTIONS.PERSONAL_DICTIONARY)}>See your Dictionary</button>
            <button className='non-editor-button button' onClick={(() => promptGoToLine())}>To Line #</button>
        </div>
        <div style={{ overflowY: 'auto', maxHeight: 'calc(75vh - 100px)' }}>
            {currentlyShowing === CURRENTLY_SHOWING_OPTIONS.MISSPELLINGS ?
                (isDone ? (misspellings.size > 0 ?
                    misspellingEntries :
                    <div className='no-misspellings'>No misspellings found.</div>) :
                    <div className="center-spinner spinner" />
                )
                : currentlyShowing === CURRENTLY_SHOWING_OPTIONS.PERSONAL_DICTIONARY ?
                    <div className="dictionary">
                        {personalDictionary.length > 0 ? personalDictionary.map(w =>
                            <div className='entry' >
                                <div className='word' onClick={() => remove(w)}>{w}&nbsp;
                                    <span style={{ color: 'red' }}>X</span>
                                </div>
                            </div>
                        ) :
                            <div>Nothing in your personal dictionary.</div>
                        }
                    </div>
                    : <></>
            }
        </div>
    </div>
}

export default SpellCheck;

const MisspellingEntry = (props) => {

    const [showSuggestions, toggleSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState(-1);

    // I wanted to set this on load, but it was too slow.
    useEffect(() => {
        if (showSuggestions) {
            const newSuggestions = props.dictionary.suggest(props.k);
            setSuggestions(newSuggestions);
        }
    }, [showSuggestions]);

    const copyToClipboard = (word) => {
        const clipboardItem = new ClipboardItem({ 'text/plain': word });
        window.navigator.clipboard.write([clipboardItem])
            .then(() => {
                props.copySuccessful();
                toggleSuggestions(false);
            })
            .catch(err => {
                alert('Failed to copy suggestion: ' + err);
            });
    }

    const getLetter = (n) => {
        let result = '';
        while (n > 0) {
            n--;
            result = String.fromCharCode(97 + (n % 26)) + result;
            n = Math.floor(n / 26);
        }
        return result;
    }

    return <div className='misspelling'>
        <h6>{`${props.k} - ${props.v.length} Result${props.v.length > 1 ? 's' : ''}`}</h6>
        <button className='non-editor-button button' onClick={() => props.addToDictionary(props.k)}>Add to Dictionary</button>
        <button className='non-editor-button button instances' onClick={() => toggleSuggestions(s => !s)}>{showSuggestions ? 'Show Instances' : 'Show Suggestions'}</button>
        {showSuggestions ?
            <div>{suggestions === -1 ? <div className="center-spinner spinner" /> :
                suggestions.length > 0 ? <ul>{suggestions.map((s, i) =>
                    <li key={`${s}-${i}`} className='suggestion'>
                        <span>{s}</span>
                        <button className='non-editor-button button copy' onClick={() => copyToClipboard(s)}>Copy</button>
                    </li>
                )}
                </ul>
                    : <div>No suggestions found.</div>
            }
            </div>
            :
            <ul>{props.goods.map((val, i) =>
                <li key={`${val}-${i}`}>
                    <span>{`${val.lineNumber ?? getLetter(i + 1)} - `}</span>
                    <span title={props.spellcheckTooltip} className={"instance " + (val.canNavigate && ' enabled')} onClick={() => props.selectText(val)}                                        >
                        {val.context}
                    </span>
                </li>)}
            </ul>
        }
    </div>
}
